import { ANSWER_GROUP_REPLY_TYPE, DEGREE_TYPE_LOOKUP, PERMISSION } from "lib/constants";
import PropTypes from "prop-types";

export const alphaNumeric = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);
export const alphaNumBool = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
]);

export const profileCompletenessShape = PropTypes.shape({
    tasks: PropTypes.shape({
        addJobs: PropTypes.bool.isRequired,
        jobDescriptions: PropTypes.bool.isRequired,
        personalSummary: PropTypes.bool.isRequired,
    }).isRequired,
    numberComplete: PropTypes.number.isRequired,
    allComplete: PropTypes.bool.isRequired,
    jobCount: PropTypes.number.isRequired,
    strength: PropTypes.number.isRequired,
    experienceYears: PropTypes.string.isRequired,
    detailLevel: PropTypes.string.isRequired,
});

export const profileShape = PropTypes.shape({
    completeness: profileCompletenessShape.isRequired,
    profile_completeness: PropTypes.shape({
        avg_duties_chars: PropTypes.number,
        self_promo: PropTypes.string,
        work_experience_months: PropTypes.number,
    }).isRequired,
    user: PropTypes.shape({
        city: PropTypes.string,
        full_name: PropTypes.string,
        initials: PropTypes.string,
    }).isRequired,
});

export const questionAnswerShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.string.isRequired,
    months: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    has_follow_up: PropTypes.bool,
    follow_up_optional: PropTypes.bool,
    kiosk_icon: PropTypes.string,
    kiosk_svg: PropTypes.string,
    kiosk_number: alphaNumeric,
    kiosk_text: PropTypes.string,
});

export const questionProps = {
    answer: PropTypes.arrayOf(questionAnswerShape).isRequired,
    follow_up: PropTypes.string,
    follow_up_options: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string.isRequired })),
        max_selections: PropTypes.number,
        min_selections: PropTypes.number,
    }),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    question: PropTypes.string.isRequired,
    image_url: PropTypes.string,
    is_required: PropTypes.bool,
    response: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            follow_up_response: PropTypes.string,
            id: PropTypes.number,
            reprompt_threshold: PropTypes.string,
            response: PropTypes.string,
            time_response: PropTypes.string,
            value: PropTypes.string,
        }),
    ]),
    submit_response: PropTypes.bool,
    time_response: PropTypes.string,
    type: PropTypes.number.isRequired,
};

export const questionShape = PropTypes.shape(questionProps);

export const answerShape = PropTypes.shape({
    id: PropTypes.number.isRequired,
    response: PropTypes.string,
    follow_up_response: PropTypes.string,
    value: PropTypes.string,
});

export const companyShape = PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    settings: PropTypes.shape({
        pipeline: PropTypes.shape({
            PENDING: PropTypes.shape({
                steps: PropTypes.arrayOf(PropTypes.string),
            }),
        }),
        resume: PropTypes.shape({
            toggle_full_name: PropTypes.bool,
        }),
    }),
    slug: PropTypes.string,
    url_slug: PropTypes.string,
    verified: PropTypes.bool,
    workforce_survey_url: PropTypes.string,
    always_on_link: PropTypes.string,
});

const positionProps = {
    active: PropTypes.bool.isRequired,
    calendar: PropTypes.number,
    capacity: PropTypes.number,
    city: PropTypes.string,
    company_name: PropTypes.string,
    company_slug: PropTypes.string,
    has_capacity_alert: PropTypes.bool,
    id: PropTypes.number,
    interview_slots: PropTypes.arrayOf(
        PropTypes.shape({
            capacity: PropTypes.number,
            duration: PropTypes.number.isRequired,
            start_time: PropTypes.string.isRequired,
        }),
    ),
    interview_type: PropTypes.oneOfType([
        PropTypes.shape({
            type: PropTypes.number.isRequired,
            short: PropTypes.string.isRequired,
            long: PropTypes.string.isRequired,
            middle: PropTypes.string.isRequired,
            action: PropTypes.string.isRequired,
        }),
        PropTypes.number,
    ]),
    link: PropTypes.string,
    location_name: PropTypes.string,
    metro: PropTypes.string,
    open_slot_count: PropTypes.number,
    role_type: PropTypes.number,
    state: PropTypes.number,
    title: PropTypes.string,
};

export const positionShape = PropTypes.shape(positionProps);

export const positionShapeFull = PropTypes.shape({
    ...positionProps,
    address: PropTypes.string,
    benefit_401k: PropTypes.bool,
    benefit_delay_days: PropTypes.number,
    benefit_dental: PropTypes.bool,
    benefit_health: PropTypes.bool,
    benefit_pto: PropTypes.bool,
    benefit_vision: PropTypes.bool,
    bonus_amount: PropTypes.number,
    bonus_description: PropTypes.string,
    description: PropTypes.string,
    hours: PropTypes.number,
    interview_address: PropTypes.string,
    interview_description: PropTypes.string,
    interview_instructions: PropTypes.string,
    interview_type: PropTypes.number,
    is_paused: PropTypes.bool,
    minimum_age_years: PropTypes.number,
    minimum_education: PropTypes.number,
    other_requirements: PropTypes.string,
    pre_interview_info: PropTypes.string,
    required_interview_items: PropTypes.number,
    required_interview_other_item: PropTypes.string,
    required_interview_wear: PropTypes.number,
    required_interview_other_wear: PropTypes.string,
    requirement_background_check: PropTypes.bool,
    requirement_drug_test: PropTypes.number,
    requirement_lift_pounds: PropTypes.number,
    wage: PropTypes.number,
});

export const facilityShape = PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    address_line1: PropTypes.string,
    address_line2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip_code: PropTypes.string,
});

export const positionMetaShape = PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
});

export const userShape = PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
    name: PropTypes.string,
    full_name: PropTypes.string,
});

export const hireShape = PropTypes.shape({
    id: PropTypes.number.isRequired,
    candidate_reported: PropTypes.bool.isRequired,
    company_name: PropTypes.string.isRequired,
    date_employed: PropTypes.string,
    date_terminated: PropTypes.string,
    time_employed: PropTypes.string,
    time_terminated: PropTypes.string,
    has_checkin_nudge: PropTypes.bool.isRequired,
    position: positionShape,
    state: PropTypes.number.isRequired,
    terminated_reason: PropTypes.number,
    user: PropTypes.shape({
        first_name: PropTypes.string.isRequired,
        full_name: PropTypes.string.isRequired,
        initials: PropTypes.string.isRequired,
        is_workforce: PropTypes.bool.isRequired,
        phone: PropTypes.string.isRequired,
    }).isRequired,
});

export const announcementShape = PropTypes.shape({
    action_state: PropTypes.number.isRequired,
    company: companyShape.isRequired,
    user: userShape.isRequired,
});

export const nudgeHireShape = PropTypes.shape({
    hire: hireShape.isRequired,
    missed_days: PropTypes.number.isRequired,
    check_ins_unresponded: PropTypes.arrayOf(PropTypes.number).isRequired,
});

export const openSlotShape = PropTypes.shape({
    capacity: PropTypes.number.isRequired,
    day: PropTypes.string.isRequired,
    duration: PropTypes.number.isRequired,
    event_id: PropTypes.number.isRequired,
    is_blackout: PropTypes.bool.isRequired,
    scheduled: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    start_moment: PropTypes.shape({}).isRequired,
    start_time: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    week: PropTypes.string.isRequired,
});

export const scheduledInterviewShape = PropTypes.shape({
    day: PropTypes.string.isRequired,
    duration: PropTypes.number.isRequired,
    interview_id: PropTypes.number.isRequired,
    location: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    position_id: PropTypes.number.isRequired,
    resume_url: PropTypes.string.isRequired,
    start_moment: PropTypes.shape({}).isRequired,
    start_time: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    week: PropTypes.string.isRequired,
});

export const calendarShape = PropTypes.shape({
    id: PropTypes.number,
    active: PropTypes.bool,
    blackout_dates: PropTypes.arrayOf(PropTypes.string),
    duration_minutes: PropTypes.number.isRequired,
    interview_count: PropTypes.number,
    max_interview_attendees: PropTypes.number,
    max_interviews_per_day: PropTypes.number,
    max_interviews_per_week: PropTypes.number,
    name: PropTypes.string,
    owner: PropTypes.number,
    timezoneAbbr: PropTypes.string,
    url: PropTypes.string,
});

export const calendarSlotShape = PropTypes.shape({
    capacity: PropTypes.number.isRequired,
    event_id: PropTypes.number.isRequired,
    is_blackout: PropTypes.bool.isRequired,
    start_time: PropTypes.string.isRequired,
    type: PropTypes.oneOf(["INTERVIEW_SLOT"]).isRequired,
});

export const interviewSlotShape = PropTypes.shape({
    interview_id: PropTypes.number.isRequired,
    is_phone_screen: PropTypes.bool.isRequired,
    is_video_call: PropTypes.bool.isRequired,
    location: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    position_id: PropTypes.number.isRequired,
    reschedule_count: PropTypes.number.isRequired,
    resume_url: PropTypes.string.isRequired,
    start_time: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.oneOf(["INTERVIEW_SCHEDULED"]).isRequired,
});

export const teamMemberShape = PropTypes.shape({
    id: PropTypes.number,
    email: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    slug: PropTypes.string,
    hire_permission_role: PropTypes.number,
    retain_permission_role: PropTypes.number,
    hire_all_access: PropTypes.bool,
    retain_all_access: PropTypes.bool,
    facility_managers: PropTypes.arrayOf(PropTypes.shape({})),
    position_managers: PropTypes.arrayOf(PropTypes.shape({})),
});

export const momentShape = PropTypes.shape({
    clone: PropTypes.func.isRequired,
    format: PropTypes.func.isRequired,
    startOf: PropTypes.func.isRequired,
    tz: PropTypes.func.isRequired,
});

export const schoolShape = PropTypes.shape({
    degree_type: PropTypes.oneOf(Object.keys(DEGREE_TYPE_LOOKUP).map(String)).isRequired,
    graduation_year: PropTypes.string,
    name: PropTypes.string.isRequired,
    // city, state?
});

export const jobShape = PropTypes.oneOfType([
    // Work Experience
    PropTypes.shape({
        city: PropTypes.string.isRequired,
        company: PropTypes.string.isRequired,
        current_job: PropTypes.number,
        duties: PropTypes.string.isRequired,
        end_date: PropTypes.string,
        reason_explain: PropTypes.string,
        reason_for_leaving: PropTypes.string,
        state: PropTypes.string.isRequired,
        start_date: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
    }),
    // Gaps
    PropTypes.shape({
        reason: PropTypes.string,
        start_date: PropTypes.string.isRequired,
        end_date: PropTypes.string.isRequired,
    }),
]);

export const referenceShape = PropTypes.shape({
    how_long_known: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    relationship: PropTypes.string.isRequired,
    message: PropTypes.string,
});

export const applicationShape = PropTypes.shape({
    badges: PropTypes.arrayOf(PropTypes.string),
    feedback_state: PropTypes.number,
    feedback_url: PropTypes.string,
    id: PropTypes.number.isRequired,
    is_expiring: PropTypes.bool.isRequired,
    jobs: PropTypes.arrayOf(jobShape),
    mismatch_ack: PropTypes.number,
    mismatch_type: PropTypes.number,
    other_apps: PropTypes.arrayOf(PropTypes.shape({})),
    pipeline_step: PropTypes.string,
    position: positionShape,
    pref_shift_start: PropTypes.string,
    questions: PropTypes.arrayOf(questionShape),
    reason: PropTypes.number,
    reason_other: PropTypes.string,
    reason_rescheduled: PropTypes.string,
    references: PropTypes.arrayOf(referenceShape),
    resume_url: PropTypes.string,
    schedule_url: PropTypes.string,
    schools: PropTypes.arrayOf(schoolShape),
    time_candidate_feedback: PropTypes.string,
    time_created: PropTypes.string,
    time_interview: PropTypes.string,
    time_interview_schedule: PropTypes.string,
    time_original_interview_schedule: PropTypes.string,
    time_reapplied: PropTypes.string,
    time_rejected: PropTypes.string,
    timezone: PropTypes.string,
    user: PropTypes.shape({
        id: PropTypes.number.isRequired,
        city: PropTypes.string,
        email: PropTypes.string,
        full_name: PropTypes.string,
        name: PropTypes.string.isRequired,
        phone: PropTypes.string,
        initials: PropTypes.string,
        prefs: PropTypes.shape({
            shift_day: PropTypes.number.isRequired,
            shift_swing: PropTypes.number.isRequired,
            shift_night: PropTypes.number.isRequired,
            shift_weekend: PropTypes.number.isRequired,
            shift_overtime: PropTypes.number.isRequired,
        }),
        move_date: PropTypes.string,
    }),
    withdraw_url: PropTypes.string,
});

export const permissionsShape = PropTypes.shape({
    [PERMISSION.CALENDAR]: PropTypes.number,
    [PERMISSION.PIPELINE]: PropTypes.number,
    [PERMISSION.PULSE]: PropTypes.number,
    [PERMISSION.ROLE_DETAILS]: PropTypes.number,
});

export const refShape = PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.elementType, PropTypes.instanceOf(window.Element)]),
});

export const checkinShape = PropTypes.shape({
    company: PropTypes.number,
    deliver_email: PropTypes.bool.isRequired,
    deliver_sms: PropTypes.bool.isRequired,
    state: PropTypes.number.isRequired,
    id: alphaNumeric.isRequired,
    milestone: PropTypes.number,
    title: PropTypes.string.isRequired,
    type: PropTypes.number.isRequired,
    subtype: PropTypes.number,
});

export const dashboardSearchOptions = PropTypes.arrayOf(
    PropTypes.shape({
        id: alphaNumeric.isRequired,
        type: PropTypes.number.isRequired,
        display: PropTypes.string,
        icon: PropTypes.node,
        fields: PropTypes.arrayOf(alphaNumeric),
        group: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                type: PropTypes.number,
            }),
        ),
        cascade: PropTypes.bool,
    }),
);

export const retainOpportunityShape = PropTypes.shape({
    category: PropTypes.string.isRequired,
    industryAvg: PropTypes.number,
    industryAvgDiff: PropTypes.number,
    oppBucket: PropTypes.number,
    retentionLift: PropTypes.number,
    retentionTotal: PropTypes.number,
    retentionRetained: PropTypes.number,
    turnoverReduction: PropTypes.number,
    totalSample: PropTypes.number,

    favorable: PropTypes.number,
    favorablePerc: PropTypes.number,
    favorableAvgScore: PropTypes.number,
    favorableRetention: PropTypes.number,
    favorableTurnover: PropTypes.number,

    unfavorable: PropTypes.number,
    unfavorablePerc: PropTypes.number,
    unfavorableAvgScore: PropTypes.number,
    unfavorableRetention: PropTypes.number,
    unfavorableTurnover: PropTypes.number,

    debug: PropTypes.shape({}),
});

export const retainSuggestedActionShape = PropTypes.shape({
    slug: PropTypes.string,
    name: PropTypes.string,
    published: PropTypes.string,
    updated: PropTypes.string,
    page_type: PropTypes.string,
    fields: PropTypes.shape({
        header_image: PropTypes.string,
        icon: PropTypes.string,
        title: PropTypes.string,
        article_content: PropTypes.string,
        themes: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
            }),
        ),
        action_group: PropTypes.shape({
            name: PropTypes.string,
        }),
    }),
});

export const impactEventShape = PropTypes.shape({
    id: PropTypes.number.isRequired,
    time_created: PropTypes.string.isRequired,
    time_modified: PropTypes.string,
    company_id: PropTypes.number.isRequired,
    user_id: PropTypes.number,
    categories: PropTypes.arrayOf(PropTypes.string).isRequired,
    event_time: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: PropTypes.string.isRequired,
    filters: PropTypes.shape({
        [PropTypes.string]: PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
        ),
    }),
});

export const impactEventShapePartial = PropTypes.shape({
    id: PropTypes.number,
    time_created: PropTypes.string,
    time_modified: PropTypes.string,
    company_id: PropTypes.number,
    user_id: PropTypes.number,
    categories: PropTypes.arrayOf(PropTypes.string),
    event_time: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    filters: PropTypes.shape({
        [PropTypes.string]: PropTypes.arrayOf(
            PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
        ),
    }),
});

export const replyShape = PropTypes.shape({
    id: PropTypes.number.isRequired,
    time_created: PropTypes.string.isRequired,
    time_modified: PropTypes.string,
    company: PropTypes.number,
    user: PropTypes.number,
    user_name: PropTypes.string,
    answer: PropTypes.number,
    answer_group: PropTypes.number,
    alert: PropTypes.number,
    type: PropTypes.number,
    reply_type: PropTypes.number,
    comment: PropTypes.string,
    time_read: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.number,
        PropTypes.string,
    ]),
});

export const replyWithGroupShape = PropTypes.shape({
    id: PropTypes.number.isRequired,
    reply_type: PropTypes.oneOf(Object.values(ANSWER_GROUP_REPLY_TYPE)).isRequired,
    answer_group: PropTypes.number.isRequired,
    content: PropTypes.string,
    time_created: PropTypes.string,
    user_name: PropTypes.string,
    time_read: PropTypes.string,
});

export const alertShape = PropTypes.shape({
    alertItems: PropTypes.arrayOf(replyShape),
    answer: PropTypes.number.isRequired,
    answer_response: PropTypes.string,
    assignee: PropTypes.number,
    assignee_name: PropTypes.string,
    category: PropTypes.string.isRequired,
    company: PropTypes.number.isRequired,
    hire: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    state: PropTypes.number.isRequired,
    time_created: PropTypes.object.isRequired,
    time_replied: PropTypes.object,
    timestamp_created: PropTypes.number.isRequired,
    timestamp_replied: PropTypes.number,
    // unread: PropTypes.bool.isRequired,
});

export const commentShape = PropTypes.shape({
    id: PropTypes.number,
    response: PropTypes.string,
    alert_id: PropTypes.number,
    category: PropTypes.string,
    type: PropTypes.number,
    state: PropTypes.number,
    assignee: PropTypes.number,
    assignee_name: PropTypes.string,
    breadcrumbs: PropTypes.arrayOf(PropTypes.string),
    comment: PropTypes.string.isRequired,
    time_created: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({})]).isRequired,
    timestamp_created: PropTypes.number,
    unread: PropTypes.bool.isRequired,
    time_replied: PropTypes.shape({}),
    timestamp_replied: PropTypes.number,
    time_resolved: PropTypes.shape({}),
    timestamp_resolved: PropTypes.number,
});

export const rosterFileShape = PropTypes.shape({
    id: PropTypes.number.isRequired,
    company: PropTypes.number.isRequired,
    original_filename: PropTypes.string.isRequired,
    total_rows: PropTypes.number,
    total_new_hires: PropTypes.number,
    total_new_terminations: PropTypes.number,
    total_emails: PropTypes.number,
    total_phones: PropTypes.number,
    total_processed: PropTypes.number,
});

export const overTimeMetricConfigShape = PropTypes.shape({
    unit: PropTypes.string,
    ticks: PropTypes.arrayOf(PropTypes.number),
    height: PropTypes.number,
    goalValue: PropTypes.number,
    goalLabel: PropTypes.string,
});

export const favorabilityByThemeDataShape = PropTypes.shape({
    category: PropTypes.string.isRequired,
    hireGroupKey: PropTypes.string,
    favorablePerc: PropTypes.number,
    avgScore: PropTypes.number,
    totalSample: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    backgroundColor: PropTypes.string.isRequired,
});

export const contactInfoShape = PropTypes.shape({
    email: PropTypes.string,
    phone: PropTypes.string,
});

export const retainFilterShape = PropTypes.shape({
    key: PropTypes.string.isRequired,
    display_name: PropTypes.string.isRequired,
    display_plural: PropTypes.string.isRequired,
    operator: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                text: PropTypes.node.isRequired,
                value: PropTypes.node.isRequired,
            }),
        ]),
    ).isRequired,
    benchmarks: PropTypes.bool,
});

export const retainNudgeFilterShape = PropTypes.shape({
    key: PropTypes.string.isRequired,
    display_name: PropTypes.string.isRequired,
    display_plural: PropTypes.string.isRequired,
    operator: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                text: PropTypes.node.isRequired,
                value: PropTypes.node.isRequired,
            }),
        ]),
    ),
    benchmarks: PropTypes.bool,
});
